import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { useRouter } from 'next/router';
import { setError } from '../../../actions';
import {
  CurrentResidenceQuery,
  ResidenceItemsQuery
} from '../../../graphql/graphql';
import { useFullPhoneNumbers } from '../../../hooks/useFullPhoneNumbers';
import { CurrentItemBreadcrumbs } from '../../Ad/Sections/CurrentItemBreadcrumbs';
import { ResidencePageSchema } from './ResidencePageSchema';
import { AnalyticLogTargetTypeEnum } from '../../../graphql/types';
import { PhotosGallery } from '../../Ad/PhotosGallery';
import { AdditionalInfoSection } from '../../Ad/Sections/AdditionalInfoSection';
import { LocationSection } from '../../Ad/Sections/LocationSection';
import { ParamsSection } from '../../Ad/Sections/ParamsSection';
import { DescriptionSection } from '../../Ad/Sections/DescriptionSection';
import { VideoSection } from '../../Ad/VideoSection';
import { InfrastructureSection } from '../../Ad/Sections/InfrastructureSection';
import { ItemsSection } from '../../Ad/Sections/ItemsSection';
import { OwnerSection } from '../../Ad/Sections/OwnerSection';
import { ApartmentsFilter } from '../../Ad/ApartmentsFilter';
import WhiteHeader from '../../WhiteHeader';
import {
  BottomPhoneWrapper,
  BreadcrumbsContainer,
  HeaderWrapper,
  MainResidenceBlock,
  OwnerBlockWrapper,
  ResidenceContent,
  ResidenceLayout,
  ResidenceTitle
} from './styled';
import { InfoViews } from '../../Ad/InfoViews';
import useScreenSize from '../../../hooks/useScreenSize';
import { MainInfo } from '../../Ad/Sections/MainInfo';
import { ErrorPage } from '../../ui/ErrorPage';
import { useClient } from '../../../hooks/useClient';
import { ResidenceApartmentTypes } from '../../Ad/ResidenceApartmentTypes';

const BottomPhoneButton = dynamic(
  () => import('../../Ad/BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false
  }
);

const mapDispatchToProps = dispatch => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

type ResidenceEntity = NonNullable<CurrentResidenceQuery['residence']>;
interface ResidenceProps {
  setErrorUI: (arg: boolean) => void;
  residence?: CurrentResidenceQuery['residence'] | null;
  residenceItems: ResidenceItemsQuery['itemsConnection']['edges'];
  residenceSlug: string;
  hasError?: boolean;
}

const ResidencePageComponent: React.FC<ResidenceProps> = ({
  setErrorUI,
  residence = null,
  residenceItems,
  residenceSlug,
  hasError = false
}) => {
  const { t } = useTranslation();
  const isClient = useClient();
  const router = useRouter();
  const { isWebView } = useBridgeState();
  const { isSmallOrLargeDesktop } = useScreenSize();

  const [planningRoomsFilter, setPlanningRoomsFilter] = useState<
    number | string
  >('all');
  const apartmentsFilterRef = useRef<{
    resetToFirstPage: () => void;
  }>(null);

  const residenceData: Partial<ResidenceEntity> = residence ?? {};

  const { onClickHandler, phonesShown, setIsVisible, isVisible } =
    useFullPhoneNumbers({
      id: residenceSlug,
      residenceId: residenceData.id ?? null,
      path: 'yasayis-kompleksleri',
      targetType: AnalyticLogTargetTypeEnum.Residence,
      agencyId: null
    });

  useEffect(() => {
    if (hasError) setErrorUI(true);
  }, [hasError]);

  const breadcrumbs = [
    {
      name: t('sections.residences.title'),
      path: '/yasayis-kompleksleri'
    },
    { name: residenceData.companyName }
  ];

  const filteredApartments = residenceData.apartments?.nodes?.filter(
    node => !!node
  );

  const handleSelectRooms = (rooms: number) => {
    setPlanningRoomsFilter(rooms);
    apartmentsFilterRef.current?.resetToFirstPage();
  };

  return (
    <>
      {residence && <ResidencePageSchema residence={residence} />}
      <ResidenceLayout $isEmpty={!residence}>
        <HeaderWrapper>
          <WhiteHeader isShowBackButton />
        </HeaderWrapper>
        {!residence && (
          <ErrorPage
            title={t('pages.residence.residence_not_found_title')}
            buttonName={t('pages.residence.residence_not_found_link')}
            onClick={() => router.push('/yasayis-kompleksleri')}
          />
        )}
        {!!residence && (
          <ResidenceContent>
            {!isWebView && (
              <BreadcrumbsContainer>
                <CurrentItemBreadcrumbs breadcrumbs={breadcrumbs} />
                <InfoViews hits={residenceData.hits} />
              </BreadcrumbsContainer>
            )}
            <ResidenceTitle tag="h1">{residenceData.name}</ResidenceTitle>
            <PhotosGallery
              residence={residenceData}
              priceMin={residenceData.price?.total?.minimum}
              currency={residenceData.price?.currency}
              {...{ onClickHandler, phonesShown, setIsVisible, isVisible }}
            />
            {!!residenceData.apartmentTypes?.length && (
              <ResidenceApartmentTypes
                apartmentTypes={residenceData.apartmentTypes}
                handleSelectRooms={handleSelectRooms}
              />
            )}
            <MainResidenceBlock>
              <div>
                <MainInfo
                  companyName={residenceData.companyName}
                  logoUrl={residenceData.logoUrl}
                  deadline={residenceData.deadline}
                  priceMin={residenceData.price?.total?.minimum}
                  currency={residenceData.price?.currency}
                  apartments={filteredApartments}
                  hasBillOfSale={residenceData.hasBillOfSale}
                  buildingsCount={residenceData.buildingsCount}
                  floorsPerBuilding={residenceData.floorsPerBuilding}
                  apartmentsCount={residenceData.apartmentsCount}
                />

                {!!residenceData.specialOffers?.length && (
                  <AdditionalInfoSection offers={residenceData.specialOffers} />
                )}
                <ParamsSection residenceData={residenceData} />
                <DescriptionSection description={residenceData.description} />
              </div>

              <OwnerBlockWrapper>
                <OwnerSection
                  ownerName={residenceData.name}
                  imageUrl={residenceData.logoUrl}
                  phonesMasked={residenceData.phonesMasked}
                  contactEmail={residenceData.contactEmail}
                  website={residenceData.website}
                  clickHandler={onClickHandler}
                  schedule={residenceData.schedule}
                  city={residenceData.city?.name}
                  address={residenceData.address}
                  deadline={residenceData.deadline}
                  priceMin={residenceData.price?.total?.minimum}
                  currency={residenceData.price?.currency}
                  apartments={filteredApartments}
                  hasBillOfSale={residenceData.hasBillOfSale}
                  {...{ phonesShown, residenceSlug, setIsVisible, isVisible }}
                />
              </OwnerBlockWrapper>
            </MainResidenceBlock>

            <LocationSection
              city={residenceData.city?.name}
              address={residenceData.address}
              locations={residenceData.locations}
              lat={residenceData.lat}
              lng={residenceData.lng}
            />

            {(residenceData.youtubeVideoId ||
              !!residenceData.constructionProgresses?.length) && (
              <VideoSection
                videoId={residenceData.youtubeVideoId}
                construction={residenceData.constructionProgresses}
                name={residenceData.name}
                companyName={residenceData.companyName}
                priceMin={residenceData.price?.total?.minimum}
                currency={residenceData.price?.currency}
              />
            )}
            {!!residenceData.infrastructures?.length && (
              <InfrastructureSection
                infrastructures={residenceData.infrastructures}
              />
            )}
            {!!filteredApartments?.length && (
              <ApartmentsFilter
                ref={apartmentsFilterRef}
                name={residenceData.name}
                apartmentRooms={residenceData.apartmentRooms}
                apartments={filteredApartments}
                phones={phonesShown}
                currentFilter={planningRoomsFilter}
                setCurrentFilter={setPlanningRoomsFilter}
                {...{ setIsVisible, isVisible, onClickHandler }}
              />
            )}
            {!!residenceData.itemsCount && (
              <ItemsSection
                residenceId={residenceData.id}
                name={residenceData.name}
                itemsRooms={residenceData.itemRooms}
                itemsCount={residenceData.itemsCount}
                residenceItems={residenceItems}
              />
            )}
            {(!isSmallOrLargeDesktop || !isClient) && (
              <BottomPhoneWrapper>
                <BottomPhoneButton
                  stat="residence-phones-btn"
                  phones={phonesShown}
                  clickHandler={onClickHandler}
                  {...{ setIsVisible, isVisible }}
                />
              </BottomPhoneWrapper>
            )}
          </ResidenceContent>
        )}
      </ResidenceLayout>
    </>
  );
};

export const ResidencePage = connect(
  null,
  mapDispatchToProps
)(ResidencePageComponent);
